import React, { useMemo, useState } from 'react';
import { Alert, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { usePermissions } from '../../../hooks/usePermissions';
import { getSalesId, getSalesValue, useSalesApiList } from '../hooks/useSalesApiResource';

const autoPickOptions = [
  { label: 'AUTO', value: 1 },
  { label: 'YES', value: 2 },
  { label: 'NO', value: 3 },
];

const getAutopickBadgeClass = (value) => `sales-autopick-badge status-${value || 3}`;
const getAutopickLabel = (value) => autoPickOptions.find((option) => option.value === Number(value))?.label || 'NO';
const normalizeCategory = (category = {}) => ({
  id: Number(getSalesId(category) || 0),
  categoryDescription: String(getSalesValue(category, 'description', 'Description', 'name', 'Name') || ''),
});
const normalizeProductFamily = (family = {}) => ({
  id: Number(getSalesId(family) || 0),
  shortName: String(getSalesValue(family, 'shortName', 'ShortName', 'typeName', 'TypeName') || ''),
});
const normalizeMapping = (mapping = {}) => ({
  id: Number(getSalesId(mapping) || 0),
  categoryId: Number(getSalesValue(mapping, 'categoryId', 'CategoryId') || 0),
  productFamilyId: Number(getSalesValue(mapping, 'productFamilyId', 'ProductFamilyId') || 0),
  isIncluded: Number(getSalesValue(mapping, 'isIncluded', 'IsIncluded') || 3),
});
const normalizeCategories = (rows) => rows.map(normalizeCategory);
const normalizeProductFamilies = (rows) => rows.map(normalizeProductFamily).filter((family) => family.id && family.shortName);
const normalizeMappings = (rows) => rows.map(normalizeMapping);

const AutopickCategoryList = () => {
  const { canEdit } = usePermissions();
  const canEditSales = canEdit('sales.container');
  const [searchTerm, setSearchTerm] = useState('');
  const [editState, setEditState] = useState(null);
  const [actionError, setActionError] = useState('');

  const { items: categories, loading: categoriesLoading, error: categoriesError } = useSalesApiList((endpoints) => endpoints.categories.base, {
    normalize: normalizeCategories,
  });
  const { items: productFamilies, loading: familiesLoading, error: familiesError } = useSalesApiList((endpoints) => endpoints.productFamilies.base, {
    normalize: normalizeProductFamilies,
  });
  const {
    items: mappings,
    setItems: setMappings,
    loading: mappingsLoading,
    error: mappingsError,
    request,
    endpoints,
  } = useSalesApiList((salesEndpoints) => salesEndpoints.categoryMappings.base, {
    normalize: normalizeMappings,
  });

  const mappingByPair = useMemo(() => {
    const map = new Map();
    mappings.forEach((mapping) => map.set(`${mapping.categoryId}:${mapping.productFamilyId}`, mapping));
    return map;
  }, [mappings]);

  const filteredRows = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    return categories.filter((row) => row.categoryDescription.toLowerCase().includes(needle));
  }, [categories, searchTerm]);

  const handleChange = async (categoryId, familyId, value) => {
    const existing = mappingByPair.get(`${categoryId}:${familyId}`);
    const payload = {
      ...(existing || {}),
      Id: existing?.id || undefined,
      CategoryId: categoryId,
      ProductFamilyId: familyId,
      IsIncluded: value,
    };
    setActionError('');

    try {
      const response = await request({
        method: existing?.id ? 'PUT' : 'POST',
        url: existing?.id ? endpoints.categoryMappings.byId(existing.id) : endpoints.categoryMappings.base,
        data: payload,
      });
      const nextMapping = normalizeMapping(response && getSalesId(response) ? response : payload);
      setMappings((current) => {
        const withoutExisting = current.filter((mapping) => !(mapping.categoryId === categoryId && mapping.productFamilyId === familyId));
        return [...withoutExisting, nextMapping];
      });
      setEditState(null);
    } catch {
      setActionError('Unable to save category auto pick value.');
    }
  };

  const loading = categoriesLoading || familiesLoading || mappingsLoading;

  return (
    <section className="sales-portal-page" aria-labelledby="sales-autopick-category-title">
      <Row className="mb-3">
        <Col><h4 id="sales-autopick-category-title">Category Auto Pick</h4></Col>
      </Row>
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <div className="position-relative search-input">
              <i className="bi bi-search position-absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#626262' }}></i>
              <Form.Control type="text" placeholder="Search" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} style={{ paddingLeft: '40px' }} />
            </div>
          </Form.Group>
        </Col>
      </Row>
      {(categoriesError || familiesError || mappingsError || actionError) && <Alert variant="danger">{categoriesError || familiesError || mappingsError || actionError}</Alert>}
      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr className="table-border">
                  <th style={{ minWidth: '200px', position: 'sticky', left: 0, zIndex: 2 }}>Name</th>
                  {productFamilies.map((family) => <th key={family.id} style={{ minWidth: '140px' }}>{family.shortName}</th>)}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={productFamilies.length + 1}><div className="sales-no-data"><Spinner animation="border" size="sm" /> Loading category auto pick...</div></td></tr>
                ) : filteredRows.length === 0 ? (
                  <tr><td colSpan={productFamilies.length + 1}><div className="sales-no-data">No Data Available</div></td></tr>
                ) : (
                  filteredRows.map((row) => (
                    <tr key={row.id} className="table-border">
                      <td style={{ position: 'sticky', left: 0, background: 'white', zIndex: 1 }}>{row.categoryDescription}</td>
                      {productFamilies.map((family) => {
                        const value = mappingByPair.get(`${row.id}:${family.id}`)?.isIncluded || 3;
                        const isEditing = editState?.categoryId === row.id && editState?.familyId === family.id;
                        return (
                          <td key={family.id}>
                            {isEditing ? (
                              <Form.Select size="sm" value={value} onChange={(event) => handleChange(row.id, family.id, parseInt(event.target.value, 10))} style={{ width: '100px' }} autoFocus>
                                {autoPickOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                              </Form.Select>
                            ) : (
                              <span>
                                <span className={getAutopickBadgeClass(value)}>{getAutopickLabel(value)}</span>
                                <button type="button" className="edit-icon ms-2" title="Edit" disabled={!canEditSales} onClick={() => setEditState({ categoryId: row.id, familyId: family.id })}>
                                  <i className="bi bi-pencil-square"></i>
                                </button>
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AutopickCategoryList;
